import { DP_ATTACHMENT_KINDS, type DpAttachment } from './dpRequest.types';

export const DP_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export const DP_ATTACHMENT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;

export const DP_PDF_ONLY: readonly string[] = ['application/pdf'];

export function dpAttachmentRootPath(ownerUid: string): string {
    return `dpRequests/${ownerUid}/`;
}

export interface DpAttachmentOptions {
    allowedTypes?: readonly string[];
    min?: number;
}

export function dpAttachmentUrlMatchesPath(url: string, path: string): boolean {
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:' || parsed.hostname !== 'firebasestorage.googleapis.com') return false;
        const match = /^\/v0\/b\/[^/]+\/o\/(.+)$/.exec(parsed.pathname);
        return match !== null && decodeURIComponent(match[1]) === path;
    } catch {
        return false;
    }
}

export function isValidDpAttachment(
    value: unknown,
    ownerUid: string,
    allowedTypes: readonly string[] = DP_ATTACHMENT_CONTENT_TYPES
): value is DpAttachment {
    if (typeof value !== 'object' || value === null) return false;
    const attachment = value as Record<string, unknown>;

    const isText = (field: unknown): field is string => typeof field === 'string' && field.trim() !== '';

    return (
        (attachment.kind === undefined || (DP_ATTACHMENT_KINDS as readonly string[]).includes(attachment.kind as string)) &&
        isText(attachment.name) &&
        isText(attachment.path) &&
        attachment.path.startsWith(dpAttachmentRootPath(ownerUid)) &&
        isText(attachment.url) &&
        dpAttachmentUrlMatchesPath(attachment.url, attachment.path) &&
        !attachment.path.includes('..') &&
        typeof attachment.contentType === 'string' &&
        allowedTypes.includes(attachment.contentType) &&
        typeof attachment.size === 'number' &&
        attachment.size > 0 &&
        attachment.size <= DP_ATTACHMENT_MAX_BYTES
    );
}

export function parseDpAttachments(
    value: unknown,
    ownerUid: string,
    options: DpAttachmentOptions = {}
): { ok: true; attachments: DpAttachment[] } | { ok: false; error: string } {
    const allowedTypes = options.allowedTypes ?? DP_ATTACHMENT_CONTENT_TYPES;
    const min = options.min ?? 0;
    const list = value === undefined || value === null ? [] : value;
    if (!Array.isArray(list)) return { ok: false, error: 'Anexos inválidos.' };
    if (list.length < min) {
        return { ok: false, error: min === 1 ? 'Anexe o arquivo obrigatório.' : `Anexe pelo menos ${min} arquivos.` };
    }
    if (list.length > 5) return { ok: false, error: 'Envie no máximo 5 anexos.' };
    if (!list.every(item => isValidDpAttachment(item, ownerUid, allowedTypes))) {
        const accepted = allowedTypes.length === 1 && allowedTypes[0] === 'application/pdf' ? 'PDF' : 'PDF, JPG ou PNG';
        return { ok: false, error: `Um dos anexos não é aceito (${accepted} até 10 MB).` };
    }
    return { ok: true, attachments: list.map(pickDpAttachment) };
}

function pickDpAttachment(item: DpAttachment): DpAttachment {
    const { kind, name, url, path, contentType, size } = item;
    return kind === undefined ? { name, url, path, contentType, size } : { kind, name, url, path, contentType, size };
}
